import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import MarketHeader from "@/components/MarketHeader";
import { Card } from "@/components/ui/card";
import { fmtNum } from "@/lib/format";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/**
 * DTE badge — shows how many days until expiry with colour coding.
 *
 * IMPORTANT: parse the ISO date string by splitting on "-" to get the
 * year/month/day components and constructing a LOCAL date — NOT via
 * new Date("YYYY-MM-DD") which is interpreted as UTC midnight and gives
 * a wrong DTE for IST users (off by 1 after 5:30 PM UTC = 11 PM IST).
 */
function DteBadge({ expiry }) {
  const dte = useMemo(() => {
    if (!expiry) return null;
    const todayLocal = new Date();
    todayLocal.setHours(0, 0, 0, 0);
    // Parse as local date to avoid UTC-offset issue
    const [y, m, d] = expiry.split("-").map(Number);
    const expLocal = new Date(y, m - 1, d); // month is 0-indexed
    return Math.round((expLocal - todayLocal) / 86400000);
  }, [expiry]);

  if (dte === null) return null;

  const label = dte === 0 ? "0DTE" : `${dte}DTE`;
  const cls =
    dte === 0
      ? "bg-rose-500/20 text-rose-300 border border-rose-500/30"
      : dte <= 3
      ? "bg-amber-500/15 text-amber-300 border border-amber-500/25"
      : "bg-slate-500/15 text-slate-400 border border-white/10";

  return (
    <span className={`px-2 py-0.5 rounded-sm text-[10px] font-mono font-semibold uppercase tracking-widest ${cls}`}>
      {label}
    </span>
  );
}

/**
 * Format a delta or IV value — returns "—" when null/undefined (not a
 * hardcoded dummy), so the trader instantly knows the value is unavailable
 * rather than seeing a fake number.
 */
function fmtGreek(v, decimals = 3) {
  if (v === null || v === undefined) return "—";
  return fmtNum(v, decimals);
}

export default function OptionChain() {
  const [index, setIndex] = useState("NIFTY");
  const [data, setData] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = () =>
      api
        .get(`/market/option-chain?index=${index}`)
        .then((r) => mounted && setData(r.data))
        .catch(() => {});
    load();
    const t = setInterval(load, 2000);
    return () => {
      mounted = false;
      clearInterval(t);
    };
  }, [index]);

  /** Delta heatmap — only applies when value is non-null. */
  const heat = (v) => {
    if (v === null || v === undefined) return {};
    const intensity = Math.min(Math.abs(v), 1);
    return { backgroundColor: `rgba(0, 122, 255, ${intensity * 0.18})` };
  };

  const atmIdx = useMemo(() => {
    if (!data?.rows) return -1;
    return data.rows.findIndex((r) => Math.abs(r.strike - data.atm) < 0.01);
  }, [data]);

  return (
    <div className="flex flex-col min-h-screen" data-testid="option-chain-page">
      <MarketHeader />
      <div className="p-3 sm:p-6 space-y-4">
        {/* ── Header row ── */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight">Option Chain</h1>
            <p className="text-sm text-slate-400 mt-0.5">
              Nearest expiry by DTE. Black-Scholes Δ &amp; IV back-solved from live LTP.
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Select value={index} onValueChange={setIndex}>
              <SelectTrigger
                className="w-32 bg-[#141414] border-white/10 h-9"
                data-testid="chain-index-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#141414] border-white/10">
                <SelectItem value="NIFTY">NIFTY</SelectItem>
                <SelectItem value="SENSEX">SENSEX</SelectItem>
              </SelectContent>
            </Select>

            {/* Expiry + DTE */}
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-widest text-slate-500">Expiry</span>
              <span className="font-mono text-xs text-slate-200" data-testid="chain-expiry">
                {data?.expiry || "…"}
              </span>
              {data?.expiry && <DteBadge expiry={data.expiry} />}
            </div>

            {/* Spot */}
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-widest text-slate-500">Spot</span>
              <span className="font-mono text-xs text-blue-300" data-testid="chain-spot">
                {data?.spot != null ? fmtNum(data.spot) : "—"}
              </span>
            </div>

            {/* Live badge */}
            {data?.live && data?.broker && (
              <span className="px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-widest bg-blue-500/15 text-blue-300 border border-blue-500/20">
                Live · {data.broker}
              </span>
            )}
          </div>
        </div>

        {/* ── Chain table ── */}
        <Card className="border-white/10 bg-[#141414] rounded-sm overflow-hidden">
          <div className="max-h-[780px] overflow-auto">
            <table className="w-full text-xs" data-testid="option-chain-table">
              <thead className="bg-white/5 text-slate-500 uppercase tracking-widest text-[10px]">
                <tr>
                  <th className="text-right px-3 py-2 font-medium" colSpan="3">
                    CALLS (CE)
                  </th>
                  <th className="text-center px-3 py-2 font-medium bg-white/5">Strike</th>
                  <th className="text-left px-3 py-2 font-medium" colSpan="3">
                    PUTS (PE)
                  </th>
                </tr>
                <tr>
                  <th className="text-right px-2 py-1.5 font-medium">IV%</th>
                  <th className="text-right px-2 py-1.5 font-medium">Δ</th>
                  <th className="text-right px-2 py-1.5 font-medium">LTP</th>
                  <th className="text-center px-2 py-1.5 font-mono text-slate-400">━</th>
                  <th className="text-left px-2 py-1.5 font-medium">LTP</th>
                  <th className="text-left px-2 py-1.5 font-medium">Δ</th>
                  <th className="text-left px-2 py-1.5 font-medium">IV%</th>
                </tr>
              </thead>
              <tbody>
                {data?.rows?.map((r, i) => {
                  const isATM = i === atmIdx;
                  return (
                    <tr
                      key={r.strike}
                      className={`border-t border-white/5 ${
                        isATM ? "bg-blue-500/10" : "hover:bg-white/5"
                      }`}
                      data-testid={`chain-row-${r.strike}`}
                    >
                      {/* CE side */}
                      <td className="text-right px-2 py-1.5 font-mono text-slate-400">
                        {fmtGreek(r.ce_iv, 2)}
                      </td>
                      <td
                        className="text-right px-2 py-1.5 font-mono text-slate-300"
                        style={heat(r.ce_delta)}
                      >
                        {fmtGreek(r.ce_delta, 3)}
                      </td>
                      <td className="text-right px-2 py-1.5 font-mono text-emerald-300 font-semibold">
                        {fmtNum(r.ce_ltp)}
                      </td>

                      {/* Strike */}
                      <td
                        className={`text-center px-2 py-1.5 font-mono ${
                          isATM ? "text-blue-300 font-bold" : "text-slate-200 font-semibold"
                        }`}
                      >
                        {fmtNum(r.strike, 0)}
                      </td>

                      {/* PE side */}
                      <td className="text-left px-2 py-1.5 font-mono text-rose-300 font-semibold">
                        {fmtNum(r.pe_ltp)}
                      </td>
                      <td
                        className="text-left px-2 py-1.5 font-mono text-slate-300"
                        style={heat(r.pe_delta)}
                      >
                        {fmtGreek(r.pe_delta, 3)}
                      </td>
                      <td className="text-left px-2 py-1.5 font-mono text-slate-400">
                        {fmtGreek(r.pe_iv, 2)}
                      </td>
                    </tr>
                  );
                })}

                {data && (!data.rows || data.rows.length === 0) && (
                  <tr>
                    <td colSpan="7" className="text-center py-8 text-amber-400/90 text-sm">
                      {data.error ||
                        "No live option chain. Connect a broker with access token in Broker Settings."}
                      {data.broker ? ` (${data.broker})` : ""}
                    </td>
                  </tr>
                )}
                {!data && (
                  <tr>
                    <td colSpan="7" className="text-center py-8 text-slate-500">
                      Loading chain…
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}

